import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, ChevronUp, Search, ChevronRight, ChevronLeft, Edit2, Send, Trash2 } from "lucide-react";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import CreateNotificationModal from "../Modals/CreateNotificationModal";
import ResponseLoader from "../common/ResponseLoader";
import { CreateNotification, getAllCompanies, getAllNotification, SaveNotificationDraft, deleteNotification } from "../../requests"; // Add Delete/Update requests here
import { notifySuccess, notifyInfo, notifyError } from "../helper/Toaster";
import styles from "../../_css/NotificationManagement.module.css";
import ViewNotificationModal from "../Modals/ViewNotificationModal";

const Notification = () => {
    const [page, setPage] = useState(1);
    const [companySearch, setCompanySearch] = useState("");
    const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);

    // Modal States
    const [showCreateNotificationModal, setShowCreateNotificationModal] = useState(false);
    const [showViewNotificationModal, setShowViewNotificationModal] = useState(false);

    // Selection & Data States
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [notifications, setNotifications] = useState([]);

    // Edit & Draft States
    const [isEditing, setIsEditing] = useState(false);
    const [notificationToEdit, setNotificationToEdit] = useState(null);

    // Loading States
    const [loading, setLoading] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [draftLoading, setDraftLoading] = useState(false);

    const [totalCount, setTotalCount] = useState(0);
    const [audiences, setAudiences] = useState([]);
    const dispatch = useDispatch();

    const companyDropdownRef = useRef(null);
    const categoryDropdownRef = useRef(null);

    const rowsPerPage = 4;
    const totalPages = Math.ceil(totalCount / rowsPerPage);

    // --- Helpers to identify current selection ---
    const getSelectedNotification = () => {
        if (selectedRows.size === 1) {
            const selectedId = Array.from(selectedRows)[0];
            return notifications.find(n => (n.id || n._id) === selectedId);
        }
        return null;
    };

    const singleSelectedRow = getSelectedNotification();
    const isDraftSelected = singleSelectedRow?.status?.toLowerCase().includes("draft");


    // Handle row selection
    const toggleRowSelection = (notificationId) => {
        const newSelectedRows = new Set(selectedRows);
        if (newSelectedRows.has(notificationId)) {
            newSelectedRows.delete(notificationId);
        } else {
            newSelectedRows.add(notificationId);
        }
        setSelectedRows(newSelectedRows);
    };

    // Handle select all
    const toggleSelectAll = () => {
        if (selectedRows.size === notifications.length) {
            setSelectedRows(new Set());
        } else {
            const allIds = new Set(notifications.map(notification => notification.id || notification._id));
            setSelectedRows(allIds);
        }
    };

    // Handle view details
    const handleViewDetails = () => {
        if (singleSelectedRow) {
            setSelectedNotification(singleSelectedRow);
            setShowViewNotificationModal(true);
        } else {
            notifyInfo("Please select exactly one notification to view details");
        }
    };

    const handleEditDraft = () => {
        if (singleSelectedRow) {
            setNotificationToEdit(singleSelectedRow);
            setIsEditing(true);
            setShowCreateNotificationModal(true);
        }
    };

    const handleQuickSend = async () => {
        if (!singleSelectedRow) return;

        if (window.confirm("Are you sure you want to send this draft immediately?")) {
            setCreateLoading(true);
            dispatch(startLoader());
            try {
                // TODO: Replace with your actual Update/Send API
                // const response = await UpdateNotificationStatus(singleSelectedRow.id, 'Sent');

                // Simulating success for now
                await new Promise(resolve => setTimeout(resolve, 1000));

                notifySuccess("Draft sent successfully");
                fetchNotifications(page);
            } catch (error) {
                notifyError("Failed to send draft");
            } finally {
                setCreateLoading(false);
                dispatch(stopLoader());
            }
        }
    };

    const handleDeleteDraft = async () => {
        if (!singleSelectedRow) return;
        const notificationId = singleSelectedRow.id || singleSelectedRow._id;
        dispatch(startLoader());
        try {
            const response = await deleteNotification(notificationId);

            if (response?.success) {
                notifySuccess("Draft deleted successfully");

                const filters = {};
                if (selectedCompany) filters.audience = selectedCompany;
                if (selectedStatus) filters.status = selectedStatus;

                await fetchNotifications(page, filters);
                setSelectedRows(new Set());
            } else {
                notifyError(response?.message || "Failed to delete draft");
            }
        } catch (error) {
            console.error("Delete notification error:", error);
            notifyError(error?.response?.data?.message || "Failed to delete draft");
        } finally {
            dispatch(stopLoader());
        }
    };


    const handleViewModalClose = () => {
        setShowViewNotificationModal(false);
        setSelectedNotification(null);
    };

    const handleCloseModal = () => {
        setShowCreateNotificationModal(false);
        setIsEditing(false);
        setNotificationToEdit(null);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
                setShowCompanyDropdown(false);
            }
            if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
                setShowCategoryDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const fetchNotifications = async (pageNum = page, filters = {}) => {
        setLoading(true);
        dispatch(startLoader());
        try {
            const response = await getAllNotification();
            const result = response?.data;
            let filteredData = [...result];
            if (filters.audience) {
                filteredData = filteredData.filter(item => item.audienceType === filters.audience);
            }

            // Filter by status
            if (filters.status) {
                filteredData = filteredData.filter(item => {
                    if (filters.status === "Draft") return item.status.includes("Draft");
                    return item.status === filters.status;
                });
            }

            const startIndex = (pageNum - 1) * rowsPerPage;
            const paginatedData = filteredData.slice(startIndex, startIndex + rowsPerPage);
            setNotifications(paginatedData);
            setTotalCount(filteredData.length);
            setSelectedRows(new Set());
        } catch (error) {
            console.error('Error fetching notifications:', error);
            setNotifications([]);
            setTotalCount(0);
            setAudiences([]);
            setSelectedRows(new Set());
        }
        dispatch(stopLoader());
        setLoading(false);
    };

    useEffect(() => {
        const filters = {};
        if (selectedCompany) filters.audience = selectedCompany;
        if (selectedStatus) filters.status = selectedStatus;

        fetchNotifications(1, filters);
        setPage(1);
    }, [selectedCompany, selectedStatus]);

    const fetchCompanies = async () => {
        try {
            const response = await getAllCompanies();
            if (response.success && Array.isArray(response.data)) {
                const companyList = response.data.map(
                    (c) => c.company_name || c.name
                );
                setAudiences(["All Users", ...companyList]);
            }
        } catch (err) {
            console.error("Error fetching companies:", err);
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    const handlePageChange = async (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
            const filters = {};
            if (selectedCompany) filters.audience = selectedCompany;
            if (selectedStatus) filters.status = selectedStatus;

            await fetchNotifications(newPage, filters);
        }
    };

    const handleAddClick = () => {
        setIsEditing(false);
        setNotificationToEdit(null);
        setShowCreateNotificationModal(true);
    };

    const handleCreateNotification = async (notificationData) => {
        setCreateLoading(true);
        dispatch(startLoader());
        try {
            const response = await CreateNotification(notificationData);
            if (response?.success) {
                if (notificationData?.scheduledAt) {
                    notifySuccess("Notification scheduled successfully");
                } else {
                    notifySuccess("Sending notifications to selected users...");
                }
                handleCloseModal();
                setTimeout(() => {
                    const filters = {};
                    if (selectedCompany) filters.audience = selectedCompany;
                    if (selectedStatus) filters.status = selectedStatus;
                    fetchNotifications(page, filters);
                }, 1000);
            } else {
                notifyError(response?.message || "Failed to create notification");
            }
        } catch (error) {
            console.error('Error creating notification:', error);
            notifyError(error?.response?.data?.message || "Error creating notification");
        } finally {
            setCreateLoading(false);
            dispatch(stopLoader());
        }
    };

    const handleDraftNotification = async (notificationData) => {
        setDraftLoading(true);
        dispatch(startLoader());
        try {
            const response = await SaveNotificationDraft(notificationData);
            if (response?.success) {
                notifySuccess("Notification Saved as Draft Successfully");
                const filters = {};
                if (selectedCompany) filters.audience = selectedCompany;
                if (selectedStatus) filters.status = selectedStatus;
                await fetchNotifications(page, filters);
                handleCloseModal();
            } else {
                notifyError(response?.message || "Failed to save notification as draft");
            }
        } catch (error) {
            console.error('Error saving notification as draft:', error);
            notifyError(error?.response?.data?.message || "Error saving notification as draft");
        } finally {
            setDraftLoading(false);
            dispatch(stopLoader());
        }
    };

    const clearFilters = () => {
        setSelectedCompany(null);
        setSelectedStatus(null);
        setCompanySearch("");
    };

    const hasActiveFilters = selectedCompany || selectedStatus;

    const getDeliveryMethodsString = (deliveryMethod) => {
        if (!deliveryMethod) return "-";
        const methods = [];
        if (deliveryMethod?.includes("popup")) methods.push("Popup");
        if (deliveryMethod?.includes("banner")) methods.push("Banner");
        if (deliveryMethod?.includes("email")) methods.push("Email");
        return methods.length > 0 ? methods.join(", ") : "-";
    };

    const getUrgencyStyle = (urgency) => {
        switch (urgency) {
            case "High": return { backgroundColor: "#FEF2F2", color: "#DC2626" };
            case "Medium": return { backgroundColor: "#FFFBEB", color: "#D97706" };
            case "Low": return { backgroundColor: "#F0FDF4", color: "#16A34A" };
            default: return { backgroundColor: "#F3F4F6", color: "#6B7280" };
        }
    };

    const getTruncatedMessage = (message) => {
        if (!message) return "-";
        return message.length > 50 ? `${message.substring(0, 50)}...` : message;
    };

    const isAllSelected = notifications.length > 0 && selectedRows.size === notifications.length;

    return (
        <div>
            <div className={styles.notificationContainer}>
                {/* Header */}
                <div className={styles.notificationHeader}>
                    <div className={styles.filterContainer}>
                        {/* Type dropdown */}
                        <div className={styles.dropdownWrapper} ref={companyDropdownRef}>
                            <button
                                onClick={() => {
                                    setShowCompanyDropdown(!showCompanyDropdown);
                                    setShowCategoryDropdown(false);
                                }}
                                className={styles.dropdownButton}
                                style={{
                                    color: selectedCompany ? "#23B4C3" : "#6b7280",
                                    borderColor: selectedCompany ? "#23B4C3" : "#D1D5DB"
                                }}
                            >
                                {selectedCompany || "Type"}
                                {showCompanyDropdown ? (
                                    <ChevronUp size={16} color={selectedCompany ? "#23B4C3" : "#6b7280"} />
                                ) : (
                                    <ChevronDown size={16} color={selectedCompany ? "#23B4C3" : "#6b7280"} />
                                )}
                            </button>

                            {showCompanyDropdown && (
                                <div className={styles.dropdownMenu}>
                                    <div className={styles.searchContainer}>
                                        <div className={styles.searchInputWrapper}>
                                            <input
                                                type="text"
                                                placeholder="Search type..."
                                                value={companySearch}
                                                onChange={(e) => setCompanySearch(e.target.value)}
                                                className={styles.searchInput}
                                            />
                                            <Search size={16} color="#6B7280" className={styles.searchIcon} />
                                        </div>
                                    </div>
                                    {audiences
                                        .filter((item) => item && item.toLowerCase().includes(companySearch.toLowerCase()))
                                        .map((item, idx) => (
                                            <div
                                                key={idx}
                                                className={`${styles.dropdownOption} ${selectedCompany === item ? styles.dropdownOptionActive : ''}`}
                                                onClick={() => {
                                                    setSelectedCompany(item);
                                                    setShowCompanyDropdown(false);
                                                }}
                                            >
                                                {item}
                                            </div>
                                        ))}
                                </div>
                            )}
                        </div>

                        {/* Status dropdown */}
                        <div className={styles.dropdownWrapper} ref={categoryDropdownRef}>
                            <button
                                onClick={() => {
                                    setShowCategoryDropdown(!showCategoryDropdown);
                                    setShowCompanyDropdown(false);
                                }}
                                className={styles.dropdownButton}
                                style={{
                                    color: selectedStatus ? "#23B4C3" : "#6b7280",
                                    borderColor: selectedStatus ? "#23B4C3" : "#D1D5DB"
                                }}
                            >
                                {selectedStatus || "Status"}
                                {showCategoryDropdown ? (
                                    <ChevronUp size={16} color={selectedStatus ? "#23B4C3" : "#6b7280"} />
                                ) : (
                                    <ChevronDown size={16} color={selectedStatus ? "#23B4C3" : "#6b7280"} />
                                )}
                            </button>

                            {showCategoryDropdown && (
                                <div className={styles.dropdownMenu}>
                                    {["Sent", "Scheduled", "Draft"].map((item, idx) => (
                                        <div
                                            key={idx}
                                            className={`${styles.dropdownOption} ${selectedStatus === item ? styles.dropdownOptionActive : ''}`}
                                            onClick={() => {
                                                setSelectedStatus(item);
                                                setShowCategoryDropdown(false);
                                            }}
                                        >
                                            {item}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Clear Filters Button */}
                        {hasActiveFilters && (
                            <button
                                onClick={clearFilters}
                                className={styles.clearFilterButton}
                            >
                                Clear Filters
                            </button>
                        )}

                        {/* ACTION BUTTONS IN HEADER  */}
                        {singleSelectedRow && (
                            <div style={{ display: 'flex', gap: '10px' }}>
                                {isDraftSelected ? (
                                    // DRAFT ACTIONS
                                    <>
                                        <button
                                            onClick={handleEditDraft}
                                            className={styles.viewDetailsButton}
                                            style={{ color: '#23B4C3', borderColor: '#23B4C3' }}
                                        >
                                            <Edit2 size={14} style={{ marginRight: '6px' }} />
                                            Edit
                                        </button>
                                        {/* <button
                                            onClick={handleQuickSend}
                                            className={styles.viewDetailsButton}
                                            style={{ color: '#16A34A', borderColor: '#16A34A' }}
                                        >
                                            <Send size={14} style={{ marginRight: '6px' }} />
                                            Send
                                        </button> */}
                                        <button
                                            onClick={handleDeleteDraft}
                                            className={styles.viewDetailsButton}
                                            style={{ color: '#EF4444', borderColor: '#EF4444' }}
                                        >
                                            <Trash2 size={14} style={{ marginRight: '6px' }} />
                                            Delete
                                        </button>
                                    </>
                                ) : (
                                    // STANDARD ACTION
                                    <button
                                        onClick={handleViewDetails}
                                        className={styles.viewDetailsButton}
                                    >
                                        View Details
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    <div>
                        <button
                            onClick={handleAddClick}
                            className={styles.createButton}
                            disabled={createLoading || draftLoading}
                        >
                            {createLoading || draftLoading ? "Processing..." : "Create Notification"}
                        </button>
                    </div>
                </div>

                {/* Table Container */}
                <div className={styles.tableContainer}>
                    <table className={styles.notificationTable}>
                        <thead>
                            <tr>
                                <th className={styles.tableHeader}>
                                    <div className={styles.checkboxContainer}>
                                        <input
                                            type="checkbox"
                                            className={styles.checkbox}
                                            checked={isAllSelected}
                                            onChange={toggleSelectAll}
                                        />
                                        <span>S.L</span>
                                    </div>
                                </th>
                                {["Title", "Description", "Audience", "Urgency", "Delivery Method", "Status"].map((label, idx) => (
                                    <th key={idx} className={styles.tableHeader}>
                                        {label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className={styles.loaderCell}>
                                        <div className={styles.tableLoaderContainer}>
                                            <ResponseLoader />
                                        </div>
                                    </td>
                                </tr>
                            ) : notifications.length > 0 ? (
                                notifications.map((row, index) => {
                                    const isLastRow = index === notifications.length - 1;
                                    const urgencyStyle = getUrgencyStyle(row.urgency);
                                    const notificationId = row.id || row._id;
                                    const isSelected = selectedRows.has(notificationId);

                                    return (
                                        <tr
                                            key={notificationId}
                                            className={`${styles.tableRow} ${isSelected ? styles.selectedRow : ''}`}
                                        >
                                            <td className={`${styles.tableCell} ${styles.firstCell} ${isLastRow ? styles.lastRowFirstCell : ''}`}>
                                                <div className={styles.checkboxContainer}>
                                                    <input
                                                        type="checkbox"
                                                        className={styles.checkbox}
                                                        checked={isSelected}
                                                        onChange={() => toggleRowSelection(notificationId)}
                                                    />
                                                    <span className={styles.serialNumber}>
                                                        {(page - 1) * rowsPerPage + index + 1}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className={styles.tableCell}>
                                                <span className={styles.cellText}>{row.title}</span>
                                            </td>
                                            <td className={styles.tableCell}>
                                                <span className={styles.descriptionText} title={row.message}>
                                                    {getTruncatedMessage(row.message)}
                                                </span>
                                            </td>
                                            <td className={styles.tableCell}>
                                                <span className={styles.cellText}>{row.audienceType || row.Audience}</span>
                                            </td>
                                            <td className={styles.tableCell}>
                                                <span className={styles.urgencyBadge} style={urgencyStyle}>
                                                    {row.urgency || "Medium"}
                                                </span>
                                            </td>
                                            <td className={styles.tableCell}>
                                                <span className={styles.cellText}>
                                                    {getDeliveryMethodsString(row.deliveryMethod)}
                                                </span>
                                            </td>
                                            <td className={`${styles.tableCell} ${styles.lastCell} ${isLastRow ? styles.lastRowLastCell : ''}`}>
                                                <span className={styles.statusContainer}>
                                                    {row.status === "Sent" ? (
                                                        <>
                                                            <span className={styles.sentIcon}>
                                                                <span className={styles.checkmark} />
                                                            </span>
                                                            <span>{row.status}</span>
                                                        </>
                                                    ) : row.status === "Scheduled" ? (
                                                        <span
                                                            className={styles.scheduledStatus}
                                                            title={
                                                                row.scheduled_at
                                                                    ? `Scheduled at ${new Date(row.scheduled_at).toLocaleString("en-IN", {
                                                                        timeZone: "Asia/Kolkata"
                                                                    })}`
                                                                    : ""
                                                            }
                                                        >
                                                            {row.status}
                                                        </span>

                                                    ) : row.status.includes("Draft") ? (
                                                        <span className={styles.draftStatus}>
                                                            {row.status}
                                                        </span>
                                                    ) : (
                                                        row.status
                                                    )}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="7" className={styles.emptyCell}>
                                        <div className={styles.emptyState}>No Data To Preview</div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className={styles.paginationContainer}>
                    {totalPages > 1 && (
                        <div className={styles.paginationControls}>
                            <button
                                disabled={page === 1 || loading}
                                onClick={() => handlePageChange(page - 1)}
                                className={styles.paginationButton}
                                style={{ color: page === 1 || loading ? '#9CA3AF' : '#23B4C3' }}
                            >
                                <ChevronLeft size={16} />
                                Previous
                            </button>

                            <div className={styles.pageNumbers}>
                                {[...Array(totalPages)].map((_, idx) => {
                                    const pageNum = idx + 1;
                                    const isActive = pageNum === page;
                                    return (
                                        <button
                                            key={pageNum}
                                            disabled={loading}
                                            onClick={() => handlePageChange(pageNum)}
                                            className={`${styles.pageButton} ${isActive ? styles.activePage : ''}`}
                                        >
                                            {pageNum.toString().padStart(2, '0')}
                                        </button>
                                    );
                                })}
                            </div>

                            <button
                                disabled={page === totalPages || loading}
                                onClick={() => handlePageChange(page + 1)}
                                className={styles.paginationButton}
                                style={{ color: page === totalPages || loading ? '#9CA3AF' : '#23B4C3' }}
                            >
                                Next
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            <CreateNotificationModal
                show={showCreateNotificationModal}
                onHide={handleCloseModal}
                onCreate={handleCreateNotification}
                onDraft={handleDraftNotification}
                createLoading={createLoading}
                draftLoading={draftLoading}
                initialData={notificationToEdit}
                isEditing={isEditing}
            />
            <ViewNotificationModal
                show={showViewNotificationModal}
                onHide={handleViewModalClose}
                notification={selectedNotification}
            />
        </div>
    );
};

export default Notification;